import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './resultEntryFormula.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm, useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { fmtLocalDateTime } from '../../utils/datetime';
import { useNavPermission } from '../../hooks/useNavPermission';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Settings ▸ SDMS ▸ Result Entry Formula — the Result Entry calculation
 * ASSIGNMENT (dbo.ResultEntry_Formula, legacy/shared table), keyed by the EXACT Test + Matrix +
 * Method combination (owner decision 2026-07-28: every Result Entry row has
 * exactly one TestMethod, so no blank/fallback matching). Mirrors the legacy
 * ALPACA "Template Formula" screen shape (mapped combos + "Add Formulas"), but
 * STANDALONE — the shared "Formula Library" (dbo.SpreadSheetBuilder_Formula,
 * used by BOTH SDMS templates and this form) is its OWN full-CRUD form via the
 * generic Settings grid, not a tab here.
 *
 * Follows the SAME structure/action-bar position/validation/audit pattern as
 * every other Settings form (§18.6/§18.8): shared FormTitle (title + load-time
 * + audit chip), shared AppToolbar (canonical New/Edit/Delete/Refresh/Export
 * order), shared useSaveFlow/useDeleteFlow ("Do you want to Save?" → save →
 * "Saved successfully." / danger-confirm → "Deleted."), server-authoritative
 * validation + 403 on missing Write, and audit via IAuditTrailService
 * (Create/Update/Delete). Exceptions are captured automatically by the global
 * ExceptionHandlingMiddleware into dbo.AI_ExceptionLog — no per-form code needed.
 */

interface MapRow {
  testMethodOid: string; test: string | null; matrix: string | null; method: string | null;
  formulaCount: number; isActive: string; modifiedAt: string | null; modifiedBy: string | null;
}
interface LibRow { formulaUqId: number; formulaID: string | null; formulaName: string; kind: number; targetField: string | null; series: number }
interface Combo  { testMethodOid: string; test: string | null; matrix: string | null; method: string | null }
interface DetailRow { formulaUqId: number; executionOrder: number }

export default function ResultEntryFormulaPage({ onHome }: { onHome?: () => void }) {
  const { alert } = useConfirm();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const perm = useNavPermission('Result Entry');

  const [maps, setMaps] = useState<MapRow[]>([]);
  const [lib, setLib] = useState<LibRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [editor, setEditor] = useState<{ combo: Combo | null; picked: Map<number, number>; viewOnly: boolean } | null>(null);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [comboSearch, setComboSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [m, l] = await Promise.all([
        api.get('/api/v1/result-entry-formula/mappings', { silentLoading: true } as never),
        api.get('/api/v1/result-entry-formula/library', { silentLoading: true } as never),
      ]);
      setMaps((m.data as { data: MapRow[] }).data ?? []);
      setLib((l.data as { data: LibRow[] }).data ?? []);
    } catch {
      notify.error('Failed to load Result Entry Formula mappings.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return maps;
    return maps.filter(m => [m.test, m.matrix, m.method].some(v => (v ?? '').toLowerCase().includes(q)));
  }, [maps, search]);

  const openEditor = useCallback(async (existing?: MapRow, viewOnly = false) => {
    try {
      if (!viewOnly && combos.length === 0) {
        const r = await api.get('/api/v1/result-entry-formula/test-methods');
        setCombos((r.data as { data: Combo[] }).data ?? []);
      }
      const picked = new Map<number, number>();
      let combo: Combo | null = null;
      if (existing) {
        combo = { testMethodOid: existing.testMethodOid, test: existing.test, matrix: existing.matrix, method: existing.method };
        const d = await api.get(`/api/v1/result-entry-formula/mappings/${existing.testMethodOid}`);
        for (const row of ((d.data as { data: DetailRow[] }).data ?? [])) picked.set(row.formulaUqId, row.executionOrder);
      }
      setComboSearch('');
      setEditor({ combo, picked, viewOnly });
    } catch {
      notify.error('Could not open the formula editor.');
    }
  }, [combos.length, notify]);

  const saveEditor = useCallback(async () => {
    if (!editor?.combo) { void alert({ title: 'Formula Setup', message: 'Select a Test / Matrix / Method first.', tone: 'warning' }); return; }
    if (editor.picked.size === 0) { void alert({ title: 'Formula Setup', message: 'Pick at least one formula.', tone: 'warning' }); return; }
    const isNew = !maps.some(m => m.testMethodOid === editor.combo!.testMethodOid);
    const formulas = [...editor.picked.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([formulaUqId, executionOrder]) => ({ formulaUqId, executionOrder }));

    await runSave({
      formName: 'Result Entry Formula',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        await api.put(`/api/v1/result-entry-formula/mappings/${editor.combo!.testMethodOid}`, {
          testMethodOid: editor.combo!.testMethodOid,
          test: editor.combo!.test ?? '', matrix: editor.combo!.matrix ?? '', method: editor.combo!.method ?? '',
          formulas,
        });
      },
      onSaved: () => { setEditor(null); void load(); },
    });
  }, [editor, maps, runSave, load, alert]);

  const deleteSelected = useCallback(async () => {
    const rows = maps.filter(m => selected.has(m.testMethodOid));
    if (rows.length === 0) { void alert({ title: 'Formula Setup', message: 'Select the mapping(s) to delete first.', tone: 'warning' }); return; }
    await runDelete({
      formName: 'Result Entry Formula',
      message: `Delete ${rows.length} mapping${rows.length === 1 ? '' : 's'}?`,
      delete: async () => { for (const r of rows) await api.delete(`/api/v1/result-entry-formula/mappings/${r.testMethodOid}`); },
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [maps, selected, runDelete, load, alert]);

  const mapCols: AppColumn<MapRow>[] = [
    { field: 'test',         header: 'Test',            width: 220, text: r => r.test ?? '' },
    { field: 'matrix',       header: 'Matrix',          width: 140, text: r => r.matrix ?? '' },
    { field: 'method',       header: 'Method',          width: 160, text: r => r.method ?? '' },
    { field: 'formulaCount', header: 'No. of Formulas', width: 130, text: r => String(r.formulaCount) },
    { field: 'isActive',     header: 'Is Active',       width: 100, text: r => r.isActive },
    { field: 'modifiedAt',   header: 'Modified',        width: 150, text: r => r.modifiedAt ? fmtLocalDateTime(r.modifiedAt) : '' },
    { field: 'modifiedBy',   header: 'Modified By',     width: 160, text: r => r.modifiedBy ?? '' },
    {
      field: 'testMethodOid', header: 'View', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => (
        <button className="ref-view-btn" title="View assigned formulas" aria-label="View"
          onClick={e => { e.preventDefault(); e.stopPropagation(); void openEditor(r, true); }}>
          <i className="ti ti-eye" style={{ fontSize: 16 }} />
        </button>
      ),
    },
  ];

  const comboMatches = useMemo(() => {
    const q = comboSearch.trim().toLowerCase();
    if (!q) return combos.slice(0, 50);
    return combos.filter(c =>
      (c.test ?? '').toLowerCase().includes(q) ||
      (c.matrix ?? '').toLowerCase().includes(q) ||
      (c.method ?? '').toLowerCase().includes(q)).slice(0, 50);
  }, [combos, comboSearch]);

  const selectedRow = maps.find(m => selected.has(m.testMethodOid));

  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(!!editor, { onClose: () => setEditor(null) });

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>SDMS</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Result Entry Express Formula Setup</span>
        </div>
        {/* Standard slim header (as Settings ▸ Utility ▸ Client): the title lives in the top bar, so FormTitle sits in
            .dp-title-row inside .mlv-hdr--empty — loose in .bl-top it left an empty band — and the "N items" caption is
            gone (BO UI Standard v4 §4; the grid footer already shows the count). */}
        <div className="dp-page-hdr mlv-hdr--empty">
          <div className="dp-title-row">
            <FormTitle pageId="reformula" title="Result Entry Express Formula Setup" auditFormName="Result Entry Formula"
              loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={perm.canWrite ? () => void openEditor() : undefined}
            newLabel="Add Formulas"
            onEdit={perm.canWrite ? () => {
              if (!selectedRow) { void alert({ title: 'Formula Setup', message: 'Select a mapping to edit.', tone: 'warning' }); return; }
              void openEditor(selectedRow);
            } : undefined}
            onDelete={perm.canWrite ? () => void deleteSelected() : undefined}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'result-entry-formula', ...gridExportMatrix(filtered, mapCols) }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search test, matrix, method…"
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<MapRow>
            rows={filtered} columns={mapCols} getRowId={r => r.testMethodOid}
            selectable selectedIds={selected} onSelectionChange={setSelected}
            loading={loading} loadMs={loadMs}
            emptyText="No formula mappings yet — click Add Formulas."
          />
        </div>
      </div>

      {/* Add/Edit Formulas — pick the exact Test+Matrix+Method, tick formulas + order. */}
      {editor && (
        <div className="ref-overlay" onClick={() => setEditor(null)}>
          <div className="ref-dialog" onClick={e => e.stopPropagation()} {...modalKeys}>
            <div className="ref-dialog-hdr">
              <span><i className="ti ti-math-function" /> {editor.viewOnly ? 'View Formulas' : editor.combo ? 'Edit Formulas' : 'Add Formulas'}</span>
              <i className="ti ti-x ref-dialog-x" onClick={() => setEditor(null)} />
            </div>

            <div className="ref-dialog-body">
              {!editor.combo ? (
                <>
                  <input autoFocus className="ref-combo-search" placeholder="Search Test / Matrix / Method…"
                    value={comboSearch} onChange={e => setComboSearch(e.target.value)} />
                  <div className="ref-combo-list">
                    {comboMatches.map(c => (
                      <div key={c.testMethodOid} className="ref-combo-row"
                        onClick={() => setEditor(ed => ed && ({ ...ed, combo: c }))}>
                        <b>{c.test}</b> · {c.matrix} · {c.method}
                      </div>
                    ))}
                    {comboMatches.length === 0 && <div className="ref-combo-empty">No matches.</div>}
                  </div>
                </>
              ) : (
                <div className="ref-combo-current">
                  <b>{editor.combo.test}</b> · {editor.combo.matrix} · {editor.combo.method}
                  {!editor.viewOnly && !maps.some(m => m.testMethodOid === editor.combo!.testMethodOid) && (
                    <span className="ref-combo-change" onClick={() => setEditor(ed => ed && ({ ...ed, combo: null }))}>change</span>
                  )}
                </div>
              )}

              <table className="ref-formula-table">
                <thead>
                  <tr>
                    {!editor.viewOnly && <th style={{ width: 34 }} />}
                    <th>Formula</th>
                    <th>TargetField</th>
                    <th style={{ width: 90 }}>Order</th>
                  </tr>
                </thead>
                <tbody>
                  {(editor.viewOnly
                    ? lib.filter(f => editor.picked.has(f.formulaUqId))
                        .sort((a, b) => (editor.picked.get(a.formulaUqId) ?? 0) - (editor.picked.get(b.formulaUqId) ?? 0))
                    : lib
                  ).map(f => {
                    const on = editor.picked.has(f.formulaUqId);
                    return (
                      <tr key={f.formulaUqId}>
                        {!editor.viewOnly && (
                          <td className="ref-chk">
                            <input type="checkbox" checked={on} onChange={e => setEditor(ed => {
                              if (!ed) return ed;
                              const picked = new Map(ed.picked);
                              if (e.target.checked) picked.set(f.formulaUqId, f.series); else picked.delete(f.formulaUqId);
                              return { ...ed, picked };
                            })} />
                          </td>
                        )}
                        <td>{f.formulaName}
                          {f.kind === 1 && <span className="ref-script-tag">(Script)</span>}</td>
                        <td>{f.targetField}</td>
                        <td className="ref-order">
                          {on && (
                            <input type="number" className="ref-order-input" value={editor.picked.get(f.formulaUqId)} min={1}
                              disabled={editor.viewOnly}
                              onChange={e => setEditor(ed => {
                                if (!ed) return ed;
                                const picked = new Map(ed.picked);
                                picked.set(f.formulaUqId, Number(e.target.value) || f.series);
                                return { ...ed, picked };
                              })} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="ref-dialog-ftr">
              {editor.viewOnly ? (
                <button className="ca-btn" onClick={() => setEditor(null)}>Close</button>
              ) : (
                <>
                  <button className="ca-btn ca-btn-solid" onClick={() => void saveEditor()}>💾 Save</button>
                  <button className="ca-btn" onClick={() => setEditor(null)}>Cancel</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
